// Dates the flow prints for people to read ("August 15", "August 15, 2026").
//
// Formatted from the ISO string rather than with the browser's locale, for the
// same reason prices are (see money.ts): a hold date or a subscription start is
// a fact about the breakdown and the account, and a French browser reading an
// English page should not get a French date in the middle of an English
// sentence. The month names go through i18next, so a translated page gets
// translated months — and so does the order: "August 15" in English, "15 août"
// in French, "15 de agosto" in Spanish (the "{{month}} {{day}}" keys).

import i18next from "i18next";

// Exported for flowLanguage.test.ts: these keys are looked up by index, so the
// test cannot find them by reading the source's translation calls.
export const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const parse = (iso?: string | null): Date | null => {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return isNaN(ms) ? null : new Date(ms);
};

// The shared month keys are capitalised ("Octobre", "Octubre") because the old
// pages show them on their own. Inside a sentence French and Spanish write them
// in lowercase: "avant le 12 octobre", "antes del 12 de octubre".
const LOWERCASE_MONTH_LANGUAGES = ["fr", "es"];

const parts = (d: Date) => {
  const lang = i18next.language;
  const month = String(i18next.t(MONTHS[d.getMonth()]));
  const day = d.getDate();
  return {
    month: LOWERCASE_MONTH_LANGUAGES.includes(lang) ? month.toLocaleLowerCase(lang) : month,
    // French writes the first of the month as an ordinal: "1er octobre".
    day: lang === "fr" && day === 1 ? "1er" : String(day),
    year: d.getFullYear(),
  };
};

// "August 15". No year: a hold that spans new year is not a thing, and a year
// makes a reassurance read like a contract.
export const monthDay = (iso?: string | null): string => {
  const d = parse(iso);
  return d ? String(i18next.t("{{month}} {{day}}", parts(d))) : "";
};

// "August 15, 2026" — for dates that can be a year out, like a subscription
// that starts when an annual trial ends.
export const longDate = (iso?: string | null): string => {
  const d = parse(iso);
  return d ? String(i18next.t("{{month}} {{day}}, {{year}}", parts(d))) : "";
};
